import styled from "@emotion/styled";
import { breakpoints, fonts } from "@trustpilot/businessapp-patternlibrary";
import { Button, Icon, Link, Modal, Typography } from "@trustpilot/constellation-components";

import { zIndexValues } from "../constants";

const customScrollbar = {
  scrollbarColor: `var(--elevation-surface)`,

  "::-webkit-scrollbar": {
    width: 6,
  },
  "::-webkit-scrollbar-track": {
    background: "var(--elevation-surface-alt)",
    borderRadius: 2,
  },
  "::-webkit-scrollbar-thumb": {
    background: "var(--elevation-surface)",
    borderRadius: 2,
  },
  "::-webkit-scrollbar-thumb:hover": {
    background: "var(--elevation-surface)",
  },
};

const navItemsSharedStyles = {
  "&:hover, &:active, &:focus, &:focus-visible": {
    backgroundColor: "var(--action-positive-active-background)",
    color: "var(--text-default)",
    outline: "none",
    opacity: "100%",
  },
  "&:focus-visible": {
    position: "relative",
    "&::after": {
      content: "''",
      display: "block",
      height: "100%",
      width: "calc(100% - var(--CDS-spacing-xxs))",
      position: "absolute",
      top: 0,
      left: 2,
      boxSizing: "border-box",
      boxShadow: `0 0 0 var(--CDS-spacing-xxxs) var(--action-primary-background), 0 0 0 var(--CDS-spacing-xxxs) var(--action-primary-background) inset`,
    },
  },
} as React.CSSProperties;
// https://stackoverflow.com/questions/46710747/type-string-is-not-assignable-to-type-inherit-initial-unset-fixe

export const StyledSidebar = styled.nav(({}) => ({
  display: "none",
  position: "fixed",
  left: 0,
  top: 0,
  width: "80%",
  height: "100%",
  minWidth: 200,
  padding: "var(--CDS-spacing-m) var(--CDS-spacing-none)",
  background: `none repeat scroll 0 0 var(--elevation-background)`,
  zIndex: zIndexValues.z100,

  [`@media (min-width: ${breakpoints.medium}px)`]: {
    display: "flex",
    flexGrow: 1,
    height: "auto",
    position: "static",
    width: "100%",
    zIndex: zIndexValues.z70,
    padding: "var(--CDS-spacing-xs) var(--CDS-spacing-none) var(--CDS-spacing-none)",
    overflow: "auto",
    ...customScrollbar,
  },

  // Targets element added by react-outside-click-handler
  "> div": {
    width: "100%",
    display: "flex",
  },
}));

export const StyledSidebarContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

export const StyledLogoImage = styled.img({
  margin: "0 0 20px 20px",
  width: 130,
  height: 32,

  [`@media (min-width: ${breakpoints.medium}px)`]: {
    order: 2,
    width: 140,
    height: 59,
    margin: "0 0 0 20px",
    padding: "var(--CDS-spacing-none) var(--CDS-spacing-none) var(--CDS-spacing-l)",
  },
});

export const StyledScrollableInnerWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  justifyContent: "space-between",
  padding: "var(--CDS-spacing-none) var(--CDS-spacing-xs)",
  overflow: "auto",
  ...customScrollbar,

  [`@media (min-width: ${breakpoints.medium}px)`]: {
    padding: "var(--CDS-spacing-none) var(--CDS-spacing-none) var(--CDS-spacing-m)",
    overflow: "visible",
    flexShrink: 0,
  },
});

interface ExpansionArrowContainerProps {
  active: boolean;
}

export const ExpansionArrowContainer = styled.i<ExpansionArrowContainerProps>(({ active }) => [
  {
    marginLeft: "auto",
    transform: active ? "rotate(180deg)" : "none",
    display: "flex",
    alignItems: "center",
  },
]);

interface StyledListItemProps {
  hideOnLargerScreens: boolean;
}

export const StyledListItem = styled.li<StyledListItemProps>(({ hideOnLargerScreens }) => [
  {
    overflow: "hidden",
    [`@media (min-width: ${breakpoints.medium}px)`]: {
      display: hideOnLargerScreens ? "none" : "initial",
    },
  },
]);

interface StyledMenuIconContainerProps {
  iconOffsetCorrection: boolean;
}

export const StyledMenuIconContainer = styled.i<StyledMenuIconContainerProps>(({ iconOffsetCorrection }) => [
  {
    display: "flex",
    alignItems: "center",
    marginRight: 12,
    position: "relative",
    transition: "transform 150ms cubic-bezier(.4,0,.2,1)",
  },
  iconOffsetCorrection && {
    top: "-1px",
  },
]);

export const StyledBadge = styled.span({
  margin: "var(--CDS-spacing-none) var(--CDS-spacing-none) var(--CDS-spacing-none) var(--CDS-spacing-m)",
});

export const StyledFirstLevelNavItemBadge = styled.span({
  margin: "var(--CDS-spacing-none) var(--CDS-spacing-none) var(--CDS-spacing-none) var(--CDS-spacing-m)",
  display: "flex",
});

const FirstLevelContainer = styled.ul({
  listStyleType: "none",
  margin: "var(--CDS-spacing-none) var(--CDS-spacing-none) var(--CDS-spacing-xl)",
  padding: 0,
  userSelect: "none",
});

const SecondLevelContainer = styled.ul({
  listStyleType: "none",
  backgroundColor: "var(--elevation-background)",
  margin: 0,
  padding: 0,
});

const Divider = styled.hr({
  borderBottom: "none",
  borderLeft: "none",
  borderRight: "none",
  borderTop: "solid 1px var(--elevation-surface-alt)",
  margin: "var(--CDS-spacing-xs) var(--CDS-spacing-none)",
});

const firstLevelItemShared = ({ active, useSmallerFont }) => [
  {
    ...fonts["body-1"],
    fontWeight: fonts.weights.medium,
    alignItems: "center",
    display: "flex",
    padding: "5px 22px",
    color: "var(--neutral-white)",
    opacity: "80%",
    cursor: "pointer",
    marginBottom: 1,

    "&:active": {
      // https://github.com/emotion-js/emotion/issues/1275
      [StyledMenuIconContainer as any]: {
        transform: "scale(.9)",
      },
    },

    "& svg, &:focus svg": {
      fill: "var(--neutral-white)",
    },

    ...navItemsSharedStyles,

    [`@media (min-width: ${breakpoints.medium}px)`]: {
      borderRadius: 0,
    },
  },
  useSmallerFont && {
    ...fonts["body-2"],
    fontWeight: fonts.weights.medium,
  },
  active && {
    backgroundColor: "var(--action-positive-active-background)",
    opacity: "100%",
    fontWeight: fonts.weights.bold,
  },
];

interface FirstLevelItemWithSubnavProps {
  active: boolean;
  useSmallerFont: boolean;
  menuItemExpanded: boolean;
}

const FirstLevelItemWithSubnav = styled.span<FirstLevelItemWithSubnavProps>(
  ({ active, useSmallerFont, menuItemExpanded }) => [
    ...firstLevelItemShared({ active, useSmallerFont }),
    menuItemExpanded && {
      backgroundColor: "var(--elevation-background)",
      boxShadow: `inset 6px 0 0 var(--neutral-stone-98)`,
      opacity: "100%",
      fontWeight: fonts.weights.bold,
    },
    active && {
      boxShadow: `inset 6px 0 0 var(--elevation-background)`,
    },
    active &&
      !menuItemExpanded && {
        boxShadow: `inset 6px 0 0 var(--action-primary-background)`,
      },
  ],
);

interface FirstLevelItemWithoutSubnavProps {
  active: boolean;
  useSmallerFont: boolean;
}

const FirstLevelItemWithoutSubnav = styled.a<FirstLevelItemWithoutSubnavProps>(({ active, useSmallerFont }) => [
  ...firstLevelItemShared({ active, useSmallerFont }),
  active && {
    boxShadow: `inset 6px 0 0 var(--neutral-stone-98)`,
  },
]);

interface SecondLevelLinkProps {
  active: boolean;
}

const SecondLevelLink = styled.a<SecondLevelLinkProps>(({ active }) => [
  {
    ...fonts["body-2"],
    fontWeight: fonts.weights.medium,
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    padding: "5px 24px 5px 50px",
    color: "var(--text-default)",
    marginBottom: 1,
    opacity: "80%",
    ...navItemsSharedStyles,
  },
  active && {
    boxShadow: `inset 6px 0 0 var(--action-primary-background)`,
    backgroundColor: "var(--action-positive-active-background)",
    fontWeight: fonts.weights.bold,
  },
]);

export const Navigation = {
  FirstLevelContainer,
  SecondLevelContainer,
  Divider,
  FirstLevelItemWithSubnav,
  FirstLevelItemWithoutSubnav,
  SecondLevelLink,
};

export const StyledTrialTextContainer = styled.div<{ cancelled: boolean; collapseTrialInfo: boolean }>(
  ({ cancelled, collapseTrialInfo }) => [
    {
      background: `var(--CDS-color-${
        cancelled ? "semantic-data-biased-negative-low" : "base-trustpilot-brand-green-primary"
      })`,
      borderRadius: "var(--CDS-border-radius-s)",
      padding: "0 var(--CDS-spacing-xs)",
      width: `${collapseTrialInfo ? "fit-content" : "auto"}`,
      marginTop: `${collapseTrialInfo ? "var(--CDS-spacing-s)" : "var(--CDS-spacing-none)"}`,
    },
  ],
);

export const StyledActiveTrialOuter = styled.section({
  padding: "var(--CDS-spacing-m)",
  marginBottom: "var(--CDS-spacing-m)",
  borderRadius: "var(--CDS-border-radius-m)",
  background: "var(--trustpilot-brand-green-20)",
});

export const StyledActiveTrialHeadingContainer = styled.div<{ collapseTrialInfo: boolean }>(({ collapseTrialInfo }) => [
  {
    display: `${collapseTrialInfo ? "inline-block" : "inline-flex"}`,
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginBottom: "var(--CDS-spacing-m)",
  },
]);

export const VerticallyCenteredTypography = styled(Typography)({
  display: "inline-block",
  verticalAlign: "middle",
  paddingBottom: "var(--CDS-spacing-xxxs)",
});

export const StyledTrial = styled.div({
  color: "var(--action-primary-text)",
  fontSize: 12,
  letterSpacing: 0.3,
  lineHeight: "var(--CDS-spacing-m)",
  padding: 0,
  marginBottom: "var(--CDS-spacing-m)",

  [`@media (min-width: ${breakpoints.medium}px)`]: {
    color: "var(--text-default)",
  },
});

export const StyledTrialInfo = styled.div({
  lineHeight: "var(--CDS-spacing-s)",
  margin: "0 0 6px",
});

export const StyledTrialName = styled.div({
  lineHeight: "var(--CDS-spacing-m)",
  margin: "0 0 6px",
});

export const StyledTrialOuter = styled.section({
  padding: "var(--CDS-spacing-m)",
  margin: "0px 20px 70px 20px",
  borderRadius: "8px",
  background: "var(--trustpilot-brand-green-20)",
});

export const StyledHeadingContainer = styled.div({
  display: "inline-flex",
  justifyContent: "left",
  alignItems: "center",
});

export const StyledTypography = styled(Typography)({
  marginLeft: "6px",
  display: "flex",
  color: "var(--neutral-white)",
});

export const StyledInvertedTypography = styled(Typography)({
  color: "var(--neutral-white)",
});

export const StyledLink = styled(Link)({
  color: "var(--neutral-white)",
  "&:active, &:focus, &:focus-visible": {
    outlineColor: "var(--neutral-white)",
    color: "var(--neutral-white) !important",
  },
  fontSize: "14px",
});

export const CancelLinkContainer = styled.div({
  marginTop: "var(--CDS-spacing-l)",
});

export const StyledCancelLink = styled.a({
  outlineColor: "var(--neutral-white)",
  textDecoration: "underline",
  textDecorationColor: "var(--neutral-white)",
  "&:hover": {
    textDecoration: "none",
  },
});

export const StyledProgressContainer = styled.div({
  height: "4.2px",
  width: "100%",
  marginTop: "var(--CDS-spacing-s)",
  marginBottom: "var(--CDS-spacing-m)",
  display: "inline-flex",
});

export const StyledCurrentProgress = styled.div<{ width: number }>(({ width }) => [
  {
    width: `${width}%`,
    borderBottomLeftRadius: "var(--CDS-border-radius-xl)",
    borderTopLeftRadius: "var(--CDS-border-radius-xl)",
    borderBottomRightRadius: `${width === 100 ? "var(--CDS-border-radius-xl)" : "var(--CDS-border-radius-none)"}`,
    borderTopRightRadius: `${width === 100 ? "var(--CDS-border-radius-xl)" : "var(--CDS-border-radius-none)"}`,
    background: "var(--CDS-color-base-trustpilot-brand-green-primary)",
  },
]);

export const StyledRemainingProgress = styled.div<{ width: number }>(({ width }) => [
  {
    width: `${width}%`,
    borderBottomRightRadius: "var(--CDS-border-radius-xl)",
    borderTopRightRadius: "var(--CDS-border-radius-xl)",
    borderBottomLeftRadius: `${width === 100 ? "var(--CDS-border-radius-xl)" : "0px"}`,
    borderTopLeftRadius: `${width === 100 ? "var(--CDS-border-radius-xl)" : "0px"}`,
    background: "var(--trustpilot-brand-green-95)",
  },
]);

export const StyledDaysLeftContainer = styled.div({
  display: "flex",
  justifyContent: "right",
});

export const StyledInvertedIcon = styled(Icon)({
  fill: "var(--neutral-white)",
});

export const StyledBodyContainer = styled.div({
  marginTop: "var(--CDS-spacing-m)",
  marginBottom: "var(--CDS-spacing-s)",
});

export const StyledModal = styled(Modal)({
  minWidth: "912px",

  [`@media (max-width: ${breakpoints.medium}px)`]: {
    minWidth: "90%",
  },
});

export const StyledModalBody = styled(Modal.Body)({
  display: "inline-flex",
  background: "var(--decorative-complementary-yellow)",
  padding: "var(--CDS-spacing-l) var(--CDS-spacing-xl)",

  [`@media (max-width: ${breakpoints.small}px)`]: {
    display: "grid",
  },
});

export const StyledModalCustomerFooter = styled(Modal.CustomFooter)({
  display: "inline-flex",
  justifyContent: "right",
});

export const StyledModalTextContainer = styled.div({
  margin: "41.5px 0px 41.5px 56px",

  [`@media (max-width: ${breakpoints.small}px)`]: {
    margin: "var(--CDS-spacing-m)",
  },
});

export const MarginTypography = styled(Typography)({
  marginTop: "var(--CDS-spacing-m)",
});

export const MarginUnorderedList = styled.ul({
  marginTop: "var(--CDS-spacing-m)",
  listStyleType: "none",
  paddingLeft: "var(--CDS-spacing-none)",
});

export const MarginUnorderedListItem = styled.ul({
  marginBottom: "var(--CDS-spacing-xs)",
  display: "flex",
  alignItems: "center",
});

export const MarginIcon = styled(Icon)({
  display: "inline-block",
  marginRight: "var(--CDS-spacing-s)",
  marginBottom: "var(--CDS-spacing-xxxs)",
  width: "14px",
  height: "14px",
});

export const MarginButton = styled(Button)({
  marginLeft: "var(--CDS-spacing-xs)",
});

export const StyledTierOuter = styled.div({
  padding: "var(--CDS-spacing-m)",

  [`@media (min-width: ${breakpoints.medium}px)`]: {
    padding: "var(--CDS-spacing-l)",
  },
});

export const StyledTier = styled.div({
  ...fonts["body-2"],
  fontSize: 12,
  color: "var(--action-primary-background)",
  letterSpacing: 0.3,
});

export const StyledTierInfo = styled.div({
  lineHeight: "var(--CDS-spacing-s)",
  margin: "0 0 6px",
});

export const StyledTierName = styled.div({
  lineHeight: "var(--CDS-spacing-m)",
  margin: "0 0 6px",
});

export const StyledTierLink = styled.a({
  fontWeight: "bold",
  textDecoration: "underline",
  color: "var(--action-primary-text)",
  [`@media (min-width: ${breakpoints.small}px)`]: {
    color: "inherit",
  },

  ":hover": {
    color: "inherit",
  },
});

export const StyledButton = styled(Button)({
  borderRadius: "var(--border-radius-xl)",
  backgroundColor: "var(--action-positive-background)",
  borderColor: "var(--action-positive-background)",
  paddingRight: "var(--spacing-m)",
  paddingLeft: "var(--spacing-m)",
});

export const StyledStar = styled.div({
  display: "none",
  [`@media (min-width: ${breakpoints.small}px)`]: {
    width: "100%",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    [".tp-star"]: {
      transform: "scale(1.1)",
    },
  },
  [`@media (min-width: ${breakpoints.medium}px)`]: {
    display: "none",
  },
});

export const StyledIcon = styled.div({
  display: "none",
  [`@media (min-width: ${breakpoints.small}px)`]: {
    alignItems: "center",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    height: "58px",
    width: "58px",
  },
  [`@media (min-width: ${breakpoints.medium}px)`]: {
    display: "none",
  },
});
